import React, { useEffect, useState, useMemo } from "react";
import {
  Row, Col, Card, CardBody, CardTitle, Button, Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormGroup, Label, Input, Spinner, Alert
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { pb } from "../../../helpers/pocketbase_helper";
import TableContainer from "../../../components/Common/DataTableContainer";
import { formatProductoNombre } from "../../../utils/productoInventarioHelper";
import { generateInventarioExcel } from "../../../helpers/inventarioEntradasExcelHelper";

const InventarioEntradas = () => {
  document.title = "Entradas de Inventario | GraphicForce - Printesco";

  // Data states
  const [inventarios, setInventarios] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  // UI states
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Pagination states
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Modal states
  const [modalAdd, setModalAdd] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  const [form, setForm] = useState({ nombre: "", cantidad: 0 });
  const [editForm, setEditForm] = useState({ id: "", total: 0, operation: "add" });
  const [saving, setSaving] = useState(false);

  // Fetch inventory data
  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      // Fetch all active inventory records
      const resultList = await pb.collection("producto_inventario").getList(1, 1000, {
        filter: "activo = true",
        sort: "nombre"
      });

      // Apply pagination
      const startIndex = pageIndex * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedItems = resultList.items.slice(startIndex, endIndex);

      setInventarios(paginatedItems);
      setTotalRecords(resultList.items.length);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Error al cargar los datos: " + err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [pageIndex, pageSize]);

  // Column definitions
  const columns = useMemo(() => [
    {
      header: "Nombre",
      accessorKey: "nombre",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => {
        const producto = row.original;
        return formatProductoNombre(producto);
      },
    },
    {
      header: "Medida",
      accessorKey: "texto_medida",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (info) => info.getValue() || "-",
    },
    {
      header: "Total",
      accessorKey: "total",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (info) => info.getValue() || 0,
    },
    {
      header: "Reservado",
      accessorKey: "reservado",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (info) => info.getValue() || 0,
    },
    {
      header: "Solicitado",
      accessorKey: "solicitado",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (info) => info.getValue() || 0,
    },
    {
      header: "Disponible",
      accessorFn: (row) => {
        const total = row.total || 0;
        const reservado = row.reservado || 0;
        // El disponible es total - reservado (lo solicitado es meramente informativo)
        return total - reservado;
      },
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => {
        const total = row.original.total || 0;
        const reservado = row.original.reservado || 0;
        // El disponible es total - reservado (lo solicitado es meramente informativo)
        const disponible = total - reservado;
        
        return (
          <span className={disponible > 0 ? "text-success" : "text-danger"}>
            {disponible}
          </span>
        );
      }
    },
    {
      header: "Última Actualización",
      accessorKey: "updated",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (info) => {
        const date = new Date(info.getValue());
        return date.toLocaleDateString("es-ES");
      },
    },
    {
      header: "Acciones",
      accessorKey: "id",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => {
        const inventario = row.original;
        return (
          <div className="d-flex gap-1">
            <Button
              color="primary"
              size="sm"
              onClick={() => handleEditInventory(inventario)}
              title="Editar inventario"
            >
              <i className="fas fa-edit"></i>
            </Button>
          </div>
        );
      }
    },
  ], []);

  // Handlers
  const handleAdd = () => {
    setForm({ nombre: "", cantidad: 0, texto_medida: "" });
    setError("");
    setSuccess("");
    setModalAdd(true);
  };

  const handleEditInventory = (inventario) => {
    setEditForm({
      id: inventario.id,
      total: 0, // Start with 0 for add/subtract operations, user will enter the amount to add/subtract
      operation: "add"
    });
    setError("");
    setSuccess("");
    setModalEdit(true);
  };

  const handleExport = async () => {
    setExporting(true);
    setError("");
    setSuccess("");

    try {
      const allItems = await pb.collection("producto_inventario").getFullList({
        filter: "activo = true",
        sort: "nombre"
      });

      if (!allItems || allItems.length === 0) {
        setError("No hay inventario para exportar");
      } else {
        generateInventarioExcel(allItems);
        setSuccess("Inventario exportado correctamente");
      }
    } catch (err) {
      console.error("Error exporting inventory:", err);
      setError("Error al exportar el inventario: " + err.message);
    }

    setExporting(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "cantidad") {
      // Only accept integer values
      const intValue = parseInt(value, 10);
      setForm({ ...form, [name]: isNaN(intValue) ? 0 : intValue });
    } else if (name === "texto_medida") {
      // Convert texto_medida to uppercase
      setForm({ ...form, [name]: value.toUpperCase() });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    if (name === "total") {
      setEditForm({ ...editForm, [name]: parseFloat(value) || 0 });
    } else if (name === "operation") {
      // When operation changes, reset total to 0 for add/subtract, or show current total for set
      const currentInventario = inventarios.find(inv => inv.id === editForm.id);
      const newTotal = value === "set" && currentInventario ? (currentInventario.total || 0) : 0;
      setEditForm({ ...editForm, [name]: value, total: newTotal });
    } else {
      setEditForm({ ...editForm, [name]: value });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    if (!form.nombre || form.nombre.trim() === "" || form.cantidad < 0) {
      setError("Ingrese un nombre y una cantidad válida (0 o mayor)");
      setSaving(false);
      return;
    }

    try {
      const nombreTrimmed = form.nombre.trim();
      const textoMedidaTrimmed = (form.texto_medida || "").trim();
      
      // Build filter for uniqueness check: nombre + texto_medida
      let filter = `nombre = "${nombreTrimmed}" && activo = true`;
      if (textoMedidaTrimmed) {
        filter += ` && texto_medida = "${textoMedidaTrimmed}"`;
      } else {
        // If texto_medida is empty, check for records where texto_medida is null or empty
        filter += ` && (texto_medida = "" || texto_medida = null)`;
      }

      // Check if record already exists by nombre + texto_medida combination
      const existingRecord = await pb.collection("producto_inventario").getList(1, 1, {
        filter: filter
      });

      let data = {
        nombre: nombreTrimmed,
        texto_medida: textoMedidaTrimmed || null, // Use null instead of empty string
        activo: true,
      };

      if (existingRecord.items.length > 0) {
        // Update existing record - increment total
        const currentRecord = existingRecord.items[0];
        data.total = (currentRecord.total || 0) + form.cantidad;
        await pb.collection("producto_inventario").update(currentRecord.id, data);
      } else {
        // Create new record
        data.total = form.cantidad;
        await pb.collection("producto_inventario").create(data);
      }

      setSuccess("Entrada registrada exitosamente");
      setModalAdd(false);
      setPageIndex(0);
      fetchData();
    } catch (err) {
      console.error("Error saving entry:", err);
      
      // Handle uniqueness constraint violation
      if (err.status === 400 && err.data) {
        // Check if error is related to unique constraint
        const errorData = err.data;
        if (errorData.nombre || errorData.texto_medida) {
          setError("Ya existe un producto con el mismo nombre y medida. La combinación nombre-medida debe ser única.");
        } else {
          setError("Error al guardar la entrada: " + (err.message || "Error desconocido"));
        }
      } else {
        setError("Error al guardar la entrada: " + (err.message || "Error desconocido"));
      }
    }
    setSaving(false);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      // Get current record first to get the current total
      const currentRecord = await pb.collection("producto_inventario").getOne(editForm.id);
      const currentTotal = currentRecord.total || 0;
      
      if (editForm.operation === "add") {
        // Agregar cantidad al total
        const newTotal = currentTotal + editForm.total;
        await pb.collection("producto_inventario").update(editForm.id, {
          total: newTotal
        });
        setSuccess("Inventario actualizado correctamente");
      } else if (editForm.operation === "subtract") {
        // Restar cantidad del total (no permitir valores negativos)
        const newTotal = Math.max(0, currentTotal - editForm.total);
        await pb.collection("producto_inventario").update(editForm.id, {
          total: newTotal
        });
        setSuccess("Inventario actualizado correctamente");
      } else if (editForm.operation === "set") {
        // Establecer cantidad específica
        await pb.collection("producto_inventario").update(editForm.id, {
          total: editForm.total
        });
        setSuccess("Inventario actualizado correctamente");
      }

      setModalEdit(false);
      fetchData(); // Recargar datos
    } catch (err) {
      console.error("Error updating inventory:", err);
      setError("Error al actualizar el inventario: " + err.message);
    }

    setSaving(false);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs maintitle="Dashboard" title="Inventarios" breadcrumbItem="Entradas" />

          {/* Success Alert */}
          {success && (
            <Alert color="success" isOpen={!!success} toggle={() => setSuccess("")} fade={true}>
              <strong>¡Éxito!</strong> {success}
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert color="danger" isOpen={!!error} toggle={() => setError("")} fade={true}>
              <strong>Error:</strong> {error}
            </Alert>
          )}

          <Row>
            <Col>
              <Card>
                <CardBody>
                  <CardTitle className="h4 d-flex justify-content-between align-items-center">
                    Entradas de Inventario
                    <div>
                      <Button
                        color="primary"
                        size="sm"
                        className="me-2"
                        onClick={handleExport}
                        disabled={exporting}
                        title="Exportar inventario a Excel"
                      >
                        {exporting ? (
                          <Spinner size="sm" className="me-1" />
                        ) : (
                          <i className="fas fa-file-excel me-1"></i>
                        )}
                        Exportar Excel
                      </Button>
                      <Button color="primary" size="sm" onClick={handleAdd}>
                        <i className="fas fa-plus me-1"></i> Agregar
                      </Button>
                      <Button
                        color="secondary"
                        size="sm"
                        className="ms-2"
                        title="Recargar tabla"
                        onClick={fetchData}
                      >
                        <i className="fas fa-sync-alt"></i>
                      </Button>
                    </div>
                  </CardTitle>
                  <div className="table-rep-plugin" style={{ overflow: "visible" }}>
                    <div className="table-responsive mb-0" data-pattern="priority-columns" style={{ overflow: "visible" }}>
                      {loading ? (
                        <div className="text-center p-4">
                          <Spinner color="primary" />
                        </div>
                      ) : (
                        <TableContainer
                          columns={columns}
                          data={inventarios}
                          isGlobalFilter={false}
                          isPagination={true}
                          isCustomPageSize={true}
                          pageIndex={pageIndex}
                          setPageIndex={setPageIndex}
                          pageSize={pageSize}
                          setPageSize={setPageSize}
                          totalRecords={totalRecords}
                          pagination="pagination"
                          paginationWrapper='dataTables_paginate paging_simple_numbers'
                          tableClass="table-bordered table-nowrap dt-responsive w-100 dataTable no-footer dtr-inline"
                        />
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* Add Entry Modal */}
      <Modal isOpen={modalAdd} toggle={() => setModalAdd(!modalAdd)} size="lg">
        <ModalHeader toggle={() => setModalAdd(!modalAdd)}>
          Agregar Entrada
        </ModalHeader>
        <Form onSubmit={handleSave}>
          <ModalBody>
            {error && (
              <Alert color="danger" isOpen={!!error} className="mb-3">
                <strong>Error:</strong> {error}
              </Alert>
            )}
            <FormGroup>
              <Label for="nombre">Nombre *</Label>
              <Input
                type="text"
                name="nombre"
                id="nombre"
                value={form.nombre}
                onChange={handleChange}
                placeholder="Ingrese el nombre del producto"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="texto_medida">Medida</Label>
              <Input
                type="text"
                name="texto_medida"
                id="texto_medida"
                value={form.texto_medida}
                onChange={handleChange}
                onInput={(e) => {
                  // Convert to uppercase as user types
                  e.target.value = e.target.value.toUpperCase();
                }}
                placeholder="Ej: 70 X 60"
                style={{ textTransform: 'uppercase' }}
              />
            </FormGroup>
            <FormGroup>
              <Label for="cantidad">Cantidad *</Label>
              <Input
                type="number"
                name="cantidad"
                id="cantidad"
                value={form.cantidad}
                onChange={handleChange}
                placeholder="Ingrese la cantidad"
                step="1"
                min="0"
                required
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModalAdd(false)}>
              Cancelar
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-2" /> : null}
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Edit Inventory Modal */}
      <Modal isOpen={modalEdit} toggle={() => setModalEdit(!modalEdit)}>
        <ModalHeader toggle={() => setModalEdit(!modalEdit)}>
          Editar Inventario
        </ModalHeader>
        <Form onSubmit={handleEditSave}>
          <ModalBody>
            {error && (
              <Alert color="danger" isOpen={!!error} className="mb-3">
                <strong>Error:</strong> {error}
              </Alert>
            )}
            {success && (
              <Alert color="success" isOpen={!!success} className="mb-3">
                <strong>Éxito:</strong> {success}
              </Alert>
            )}
            <FormGroup>
              <Label for="operation">Operación *</Label>
              <Input
                type="select"
                name="operation"
                id="operation"
                value={editForm.operation}
                onChange={handleEditChange}
                required
              >
                <option value="add">Agregar al total</option>
                <option value="subtract">Restar del total</option>
                <option value="set">Establecer cantidad específica</option>
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="total">Cantidad *</Label>
              <Input
                type="number"
                name="total"
                id="total"
                value={editForm.total}
                onChange={handleEditChange}
                placeholder="Ingrese la cantidad"
                step="0.01"
                min="0"
                required
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModalEdit(false)}>
              Cancelar
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-2" /> : null}
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default InventarioEntradas;
